import axios from 'axios';

const BASE_URL = 'https://loyalty.bom.asia';
const API_BASE_URL = `${BASE_URL}/api/miniApp/quiz/steps`;

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

const stepApi = {
  // Get all steps with filters
  getAllSteps: async (params = {}) => {
    try {
      
      // Build query string
      const queryParams = new URLSearchParams();
      
      // Pagination
      if (params.page) queryParams.append('page', params.page);
      if (params.limit) queryParams.append('limit', params.limit);
      
      // Filters
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      if (params.stepType) queryParams.append('stepType', params.stepType);
      
      const queryString = queryParams.toString();
      const url = queryString ? `?${queryString}` : '';
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting all steps:', error);
      throw error;
    }
  },

  // Get step by ID
  getStepById: async (id) => {
    try {
      const response = await api.get(`/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting step by ID:', error);
      throw error;
    }
  },

  // Get steps by type
  getStepsByType: async (stepType, params = {}) => {
    try {
      
      const queryParams = new URLSearchParams();
      if (params.isActive !== undefined) queryParams.append('isActive', params.isActive);
      
      const queryString = queryParams.toString();
      const url = queryString ? `/type/${stepType}?${queryString}` : `/type/${stepType}`;
      
      const response = await api.get(url);
      return response.data;
    } catch (error) {
      console.error('❌ Error getting steps by type:', error);
      throw error;
    }
  },

  // Get steps statistics
  getStepsStatistics: async () => {
    try {
      const response = await api.get('/statistics');
      return response.data;
    } catch (error) {
      console.error('❌ Error getting steps statistics:', error);
      throw error;
    }
  },

  // Create steps (bulk) with sample data
  createStepsWithSample: async () => {
    try {
      const response = await api.post('/admin/steps/create');
      return response.data;
    } catch (error) {
      console.error('❌ Error creating steps with sample data:', error);
      throw error;
    }
  },

  // Create steps (bulk) with custom data
  createSteps: async (steps) => {
    try {
      const response = await api.post('/admin/steps/create', { steps });
      return response.data;
    } catch (error) {
      console.error('❌ Error creating steps:', error);
      throw error;
    }
  },

  // Update step
  updateStep: async (id, updateData) => {
    try {
      const response = await api.put(`/admin/${id}`, updateData);
      return response.data;
    } catch (error) {
      console.error('❌ Error updating step:', error);
      throw error;
    }
  },

  // Delete step
  deleteStep: async (id) => {
    try {
      const response = await api.delete(`/admin/${id}`);
      return response.data;
    } catch (error) {
      console.error('❌ Error deleting step:', error);
      throw error;
    }
  },

  // Bulk update steps
  bulkUpdateSteps: async (steps) => {
    try {
      const response = await api.put('/admin/bulk/update', { steps });
      return response.data;
    } catch (error) {
      console.error('❌ Error bulk updating steps:', error);
      throw error;
    }
  },

  // Bulk delete steps
  bulkDeleteSteps: async (stepIds) => {
    try {
      const response = await api.delete('/admin/bulk/delete', { data: { stepIds } });
      return response.data;
    } catch (error) {
      console.error('❌ Error bulk deleting steps:', error);
      throw error;
    }
  },

  // Toggle step active status
  toggleStepStatus: async (id) => {
    try {
      const response = await api.patch(`/admin/${id}/toggle`);
      return response.data;
    } catch (error) {
      console.error('❌ Error toggling step status:', error);
      throw error;
    }
  },

  // Helper: Get quiz type steps
  getQuizSteps: async (params = {}) => {
    return stepApi.getStepsByType('quiz', params);
  },

  // Helper: Get information type steps
  getInformationSteps: async (params = {}) => {
    return stepApi.getStepsByType('information', params);
  },

  // Helper: Get verification type steps
  getVerificationSteps: async (params = {}) => {
    return stepApi.getStepsByType('verification', params);
  },

  // Helper: Get active steps only
  getActiveSteps: async () => {
    return stepApi.getAllSteps({ isActive: true });
  },

  // Helper: Get inactive steps only
  getInactiveSteps: async () => {
    return stepApi.getAllSteps({ isActive: false });
  }
};

export default stepApi;

